// Minimal, dependency-free markdown for chat-bot output: **bold** spans,
// `-`/`*` bullet lines, `[[type:id|label]]` listing-reference markers, and
// `[[photo:url|label]]` photo markers — the constructs the help-chat system
// prompt asks the model to use (see gitcha-mcp's agent/prompt.py).
// Deliberately narrow, not a general markdown parser, and returns plain
// data rather than an HTML string, so callers render it as real React
// elements — no dangerouslySetInnerHTML.
//
// Listing-reference markers are intentionally NOT generic markdown links
// (`[text](url)`). The model only ever supplies an id/key; this file (via
// listingRefUrl below) is the only thing that turns an id into a URL, from
// a fixed set of three known route templates. That's a deliberate security
// boundary — model output can never make the chat UI render an arbitrary,
// attacker-influenced href.
//
// Photo markers can't use that same id->route mechanism (a photo has no
// route to resolve through - the URL itself is the resource), so the marker
// carries the URL directly. The security boundary here is instead
// photoRefUrl's host allowlist below: the model is told to copy a URL
// verbatim from an already-fetched tool result and never construct one, but
// that instruction is advisory only - the allowlist is what actually
// prevents rendering an attacker-controlled <img src>.

const BOLD_PATTERN = "\\*\\*(.+?)\\*\\*";
// Inline `code` spans. The MCP prompt emits these for tool/field names;
// without a rule here the backticks printed literally.
const CODE_PATTERN = "`([^`]+)`";
// type: listing | draft | inventory | mls. id/key: anything but `]` or `|`.
// label:
// anything but `]` (kept lenient rather than matching the prompt's stated
// "no ] or |" instruction exactly - a model that violates its own
// instructions should still degrade to a working, if oddly-labeled, link
// rather than falling through to unparsed literal text).
//
// Both captures are bounded to 300 chars (a real id/label is never close to
// that long) rather than left unbounded (`+`). Two adjacent unbounded
// quantifiers over overlapping character classes, with no closing `]]` to
// stop at, make the engine try every split between them before failing -
// quadratic in the run's length, and this is exactly the normal mid-stream
// state for a marker still being typed out. A label over 300 chars isn't a
// real label anyway; falling through to plain text is the same "degrade,
// never break" behavior this file already uses elsewhere (a bug caught in
// review).
const LISTING_REF_PATTERN =
  "\\[\\[(listing|draft|inventory|mls):([^\\]|]{1,300})\\|([^\\]]{1,300})\\]\\]";
// url: anything but `]` or `|` (a real URL never contains either). label:
// same lenient "anything but ]" as listing-ref labels. Bounded the same way
// and for the same reason as LISTING_REF_PATTERN above.
const PHOTO_REF_PATTERN = "\\[\\[photo:([^\\]|]{1,300})\\|([^\\]]{1,300})\\]\\]";
// Anchored, standalone variants used to detect a marker that is the *entire*
// content of a **bold** span (see the bold-swallows-a-marker fix below) -
// not part of the streaming INLINE_PATTERN pass.
const LISTING_REF_ONLY = new RegExp(`^${LISTING_REF_PATTERN}$`);
const PHOTO_REF_ONLY = new RegExp(`^${PHOTO_REF_PATTERN}$`);
const INLINE_PATTERN = new RegExp(
  `${BOLD_PATTERN}|${LISTING_REF_PATTERN}|${CODE_PATTERN}|${PHOTO_REF_PATTERN}`,
  "g"
);

// One line of text -> [{type: "text"|"bold"|"listing-ref"|"photo-ref", ...}],
// split on **bold** spans and [[...]] markers, in document order. A marker
// missing its closing `]]` (e.g. mid-stream, before the rest of the token
// has arrived) simply doesn't match yet and falls through as plain text
// until it does - no separate "is this a partial marker" handling needed,
// same property the existing bold-span parsing already has.
export function parseInlineMarkdown(line) {
  const segments = [];
  let lastIndex = 0;
  let match;
  INLINE_PATTERN.lastIndex = 0;
  while ((match = INLINE_PATTERN.exec(line)) !== null) {
    if (match.index > lastIndex) {
      segments.push({
        type: "text",
        value: line.slice(lastIndex, match.index),
      });
    }
    if (match[1] !== undefined) {
      // The model sometimes wraps an entire marker in **bold** (observed
      // live: "**[[inventory:332|2001 19th St]]**"). Since BOLD_PATTERN's
      // non-greedy content is still happy to consume a whole marker, it
      // would otherwise win the alternation (it starts matching two chars
      // earlier than the marker itself) and the marker would render as
      // literal bracket text inside a <strong>, never as a link/image. If
      // the bold content is *exactly* one marker and nothing else, treat it
      // as that marker instead of as bold text.
      const listingMatch = match[1].match(LISTING_REF_ONLY);
      const photoMatch = !listingMatch && match[1].match(PHOTO_REF_ONLY);
      if (listingMatch) {
        segments.push({
          type: "listing-ref",
          refType: listingMatch[1],
          refId: listingMatch[2],
          label: listingMatch[3].trim(),
        });
      } else if (photoMatch) {
        segments.push({
          type: "photo-ref",
          url: photoMatch[1],
          label: photoMatch[2].trim(),
        });
      } else {
        segments.push({ type: "bold", value: match[1] });
      }
    } else if (match[5] !== undefined) {
      segments.push({ type: "code", value: match[5] });
    } else if (match[2] !== undefined) {
      segments.push({
        type: "listing-ref",
        refType: match[2],
        refId: match[3],
        label: match[4].trim(),
      });
    } else {
      segments.push({
        type: "photo-ref",
        url: match[6],
        label: match[7].trim(),
      });
    }
    lastIndex = INLINE_PATTERN.lastIndex;
  }
  if (lastIndex < line.length) {
    segments.push({ type: "text", value: line.slice(lastIndex) });
  }
  return segments;
}

// refType -> the app route for that listing type's detail page (see
// src/constants/pageURLs.js and the routes declared in src/App.js). Kept as
// an explicit allowlist, not string interpolation from refType - the only
// three destinations a marker can ever produce.
//
// Exported so src/helpers/currentPageContext.js can recognize the same
// routes in reverse (URL -> record) without a second, driftable copy of
// this map - a route a chat card can't link to is exactly a route that
// file shouldn't report the agent as "currently on" either.
export const LISTING_REF_ROUTES = {
  listing: "/want-listing-details",
  // A draft is unpublished and unfinished, so the detail page - which is
  // where `listing` goes - is the wrong end of it: read-only, and built to
  // show a listing that is live. The copilot's whole job on a draft is to
  // hand the agent a head start and point at the screen where they finish
  // it, and that screen is the agent edit flow.
  draft: "/edit-want-listing-agent",
  inventory: "/inventory-details",
  mls: "/mls-listing",
};

// A `listing-ref` segment -> its detail-page path, or null for an unknown
// refType (defensive - INLINE_PATTERN's own alternation already restricts
// refType to the three known values, so this is a second, cheap guard
// against the parser and this map ever silently drifting apart).
export function listingRefUrl(segment) {
  const base = LISTING_REF_ROUTES[segment?.refType];
  if (!base || !segment.refId) return null;
  return `${base}/${encodeURIComponent(segment.refId)}`;
}

// The only hosts an <img src> from a photo-ref marker may ever point at.
// Confirmed directly against live Inventory_Photos data (gitcha-mcp#27) -
// all three environment buckets, no CDN in front of S3.
const PHOTO_HOST_ALLOWLIST = new Set([
  "gitcha-uploads.s3.us-east-1.amazonaws.com",
  "gitcha-stage-uploads.s3.us-east-1.amazonaws.com",
  "gitcha-dev-uploads.s3.us-east-1.amazonaws.com",
]);

// A `photo-ref` segment -> its URL if it passes the host allowlist, else
// null. This is the real security boundary for photo markers (see the
// file-level comment above) - parses with the URL constructor rather than
// string-matching the raw text, so a trick like
// "https://evil.example/gitcha-uploads.s3.us-east-1.amazonaws.com/x.jpg"
// (the allowlisted string appearing in the path, not the host) is rejected:
// parsed.hostname is genuinely just the host, immune to that class of bypass.
export function photoRefUrl(segment) {
  if (!segment?.url) return null;
  let parsed;
  try {
    parsed = new URL(segment.url);
  } catch {
    return null;
  }
  if (parsed.protocol !== "https:") return null;
  if (!PHOTO_HOST_ALLOWLIST.has(parsed.hostname)) return null;
  return parsed.href;
}

const BULLET_PATTERN = /^\s*[-*]\s+(.*)$/;
// ATX headings (#..######) and ordered list items ("1. text"). The model
// emits both; before this they rendered as literal "## Heading" / "1. item".
const HEADING_PATTERN = /^\s*(#{1,6})\s+(.*)$/;
const ORDERED_PATTERN = /^\s*(\d{1,3})[.)]\s+(.*)$/;

// A listing preview card, one per line - NOT part of INLINE_PATTERN. Unlike
// listing-ref/photo-ref (which sit inline inside a sentence), a card is
// block-level content the model is told to put alone on its own line, so it
// is detected the same way headings/bullets/ordered items are: a whole-line
// pattern checked in parseMarkdownLines, not a character-level scan. That
// keeps INLINE_PATTERN's alternation (already handling bold/code/listing-ref/
// photo-ref) from growing a tenth-and-up capture group for fields a model
// would never need to bold or nest inside prose anyway.
//
// `inventory`, `listing` (want-listing), and `mls` are supported. An `mls`
// card carries one more trailing field than the other two - `agent`, the
// listing agent's "Name, Brokerage" - and REQUIRES it non-empty (see
// parseCardLine below): MLS data display rules require listing-agent/
// brokerage attribution wherever a card shows MLS details, so a card the
// model built without that attribution must fail closed to the plain
// `[[mls:...]]` link marker (no card, no photo, no attribution claim)
// rather than render un-credited. inventory/listing never had (or need)
// this compliance requirement, so they keep their original 3 trailing
// fields (city/status/photo) unchanged.
//
// Fields, pipe-delimited, none of which may contain `|` or `]`: id, title,
// price (pre-formatted by the model - "$285,000", "$1,800/mo", or a budget
// range like "$200K-$250K" for a want-listing - not re-derived client-side,
// since sale/rent/nightly/range pricing formats differ), beds, baths, sqft,
// city, status, and photo (a URL, or the literal "none" when the listing has
// no photo/map yet - most chat-created drafts). The status alternation is
// the union of inventory's four lifecycle flags and a want-listing's own
// status_for() states - each card type only ever emits its own subset, and
// ListingCard falls back to a default badge for a status outside a given
// type's set the same way it already falls back for an unrecognized type.
//
// price/beds/baths/sqft are allowed to be empty - a want-listing's budget/
// bedrooms/bathrooms/sqft criteria are all optional search fields
// (WantListingRead has them as nullable), so a real listing can genuinely
// have none of them set. id/title/city/status/photo are never legitimately
// empty for either card type.
//
// Parsed by splitting on `|` rather than a single fixed-arity regex - a
// regex requiring exactly 9 fields failed the *entire* card on a single
// stray pipe from the model, twice in production: once when a genuinely
// blank optional field used a `+` quantifier that required at least one
// character, and again when the model itself emitted one extra empty field
// by mistake ("...|3|2|||Lubbock, TX|draft|none]]" - two blanks where only
// one was needed). id/title/city/status/photo positions are taken from the
// start/end of the split (those never legitimately vary in count); the four
// optional fields in between are padded with "" if the model supplied fewer
// than four, or - if it supplied more - have their excess *empty* entries
// dropped (never a non-empty one) until exactly four remain. This keeps the
// same strictness everywhere it matters (a required field still can't be
// blank, status is still checked against the known set) while no longer
// treating an isolated stray pipe as reason to discard a whole card.
const CARD_STATUSES = new Set([
  "draft", "published", "sold", "archived", "pending", "closed", "terminated",
  // "active" is mls-only (an MLS listing has no lifecycle Gitcha controls -
  // it's simply on-market for as long as the feed says so), but lives in
  // the same shared set as every other status rather than a separate
  // mls-only set, matching how this set was never meant to be exhaustive
  // per type in the first place (see the "each card type only ever emits
  // its own subset" note above).
  "active",
]);
// Anchored at the front only. Trailing content AFTER the closing "]]" is
// intentionally unconstrained now - a model appending a caption on the same
// line ("[[card:...]] here's a great match", a common model habit) used to
// fail this pattern entirely (it required "]]" at the exact end of the
// line via `\s*$`), which threw away the card and rendered the WHOLE line,
// marker syntax included, as raw paragraph text - worse than just dropping
// the caption (a bug caught in review). The card is what matters; any
// trailing caption is discarded (not appended as a second block) rather
// than kept, matching this file's existing "one line, one block" shape -
// chip/choice/heading lines don't spawn a second block for extra trailing
// content either.
//
// The content between the type and "]]" is now matched as a run of
// non-"]" characters rather than the previous unbounded ".+" (which relied
// on backtracking from the end of the line to find the LAST "]]" - fields
// can never legitimately contain "]" in the first place; parseCardLine's
// own guard below already rejects any field that does). "[^\]]+" can only
// ever match up to the very next "]", so there's nothing left to backtrack
// over - strictly more bounded than before, not less, so the ReDoS-bounding
// this pattern already had is preserved.
const CARD_LINE_OUTER = /^\s*\[\[card:(inventory|listing|mls):([^\]]+)\]\]/;
// mls carries one more required trailing field (agent attribution) than
// inventory/listing - see the file-level comment above.
const CARD_TRAILING_FIELD_COUNT = { inventory: 3, listing: 3, mls: 4 };

// A numeric card field the assistant filled in with a placeholder rather than
// leaving empty. The prompt asks for an empty field when a value is not set,
// and prompt compliance is probabilistic: seen live, a land draft rendered
// "{} sqft" under its price, and a budget with no floor rendered
// "$0-$150,000". Both read as values the agent never gave. Cleaned here
// because this is the one place every card passes through, and because a
// marker already streamed to the client cannot be rewritten upstream.
// "[]" is deliberately absent: a field containing "]" is rejected by the
// guard above, which fails closed to plain text rather than to a wrong card.
const CARD_FIELD_PLACEHOLDERS = new Set([
  "{}", "null", "undefined", "n/a", "-", "?", "0", "$0", "none",
]);

function cleanCardField(value) {
  const text = String(value ?? "").trim();
  // Lowercased before the lookup: the model writes these in whatever case
  // it feels like ("None", "N/A", "NULL"), and the placeholder is exactly
  // as much a non-value capitalized as it is lowercase.
  return CARD_FIELD_PLACEHOLDERS.has(text.toLowerCase()) ? "" : text;
}

function parseCardLine(line) {
  const outer = line.match(CARD_LINE_OUTER);
  if (!outer) return null;
  const refType = outer[1];
  const fields = outer[2].split("|");
  // A field may never itself contain `]` - if one snuck in, the outer
  // match's greedy `.+` swallowed more of the line than a real card marker
  // ever would (e.g. a stray "]]" inside what should've been a field), and
  // trusting that split would risk misreading which piece is which.
  if (fields.some((f) => f.includes("]"))) return null;
  const trailingCount = CARD_TRAILING_FIELD_COUNT[refType];
  if (fields.length < 2 + trailingCount) return null;
  const id = fields[0];
  const title = fields[1];
  const trailing = fields.slice(fields.length - trailingCount);
  const city = trailing[0];
  const status = trailing[1];
  const photo = trailing[2];
  const agent = trailingCount === 4 ? trailing[3] : "";
  let middle = fields.slice(2, fields.length - trailingCount);
  if (middle.length > 4) {
    let toDrop = middle.length - 4;
    middle = middle.filter((f) => {
      if (toDrop > 0 && f === "") {
        toDrop -= 1;
        return false;
      }
      return true;
    });
  }
  while (middle.length < 4) middle.push("");
  if (middle.length !== 4) return null;
  if (!id.trim() || !title.trim() || !city.trim() || !photo.trim()) return null;
  if (!CARD_STATUSES.has(status)) return null;
  // Compliance boundary, not a stylistic default: an mls card with no
  // attribution fails closed to the plain link marker instead of rendering
  // un-credited (see the file-level comment above) - the ONLY required
  // field this function checks per-type rather than universally.
  if (refType === "mls" && !agent.trim()) return null;
  return {
    refType,
    refId: id.trim(),
    title: title.trim(),
    // `price` keeps its own trim: "Budget TBD" is a legitimate value and a
    // zero price is already handled where the marker is built. The three
    // numeric fields are the ones a placeholder reaches.
    price: middle[0].trim(),
    beds: cleanCardField(middle[1]),
    baths: cleanCardField(middle[2]),
    sqft: cleanCardField(middle[3]),
    city: city.trim(),
    ...(refType === "mls" ? { agent: agent.trim() } : {}),
    status,
    photo: photo.trim(),
  };
}

// A block-level flag, on its own line immediately before a run of card
// lines, marking that run as a single-choice prompt (the model is asking
// the user to pick one, e.g. "which property would you like insights for?")
// rather than just showing results. Never rendered itself - consumed by
// groupMarkdownBlocks below, same "sentinel line, not real content" shape
// as nothing else in this file, since every other marker renders something.
const CHOICE_MARKER_PATTERN = /^\s*\[\[choice\]\]\s*$/;

// A recommendation chip, one per line - same block-level shape as a card
// (not part of INLINE_PATTERN, must be alone on its line). Unlike the
// starter suggestions (a static, hardcoded list shown only on an empty
// conversation), these are dynamic: the model emits them for a specific
// "dead end" moment - most commonly a not-found result - so the agent has
// concrete next steps instead of just a clarifying question. `<text>` is
// both the button's label AND the exact message sent when clicked (same
// "click sends this verbatim" behavior as the static starter chips) - one
// field, not a separate label/prompt pair, since a chip only ever needs to
// say what it does.
const CHIP_LINE_PATTERN = /^\s*\[\[chip:([^\]|]+)\]\]\s*$/;

// Full message text -> [{type: "bullet"|"paragraph"|"card", segments|...}],
// one entry per line, each with its inline segments already parsed.
export function parseMarkdownLines(text) {
  return (text || "").split("\n").map((line) => {
    const headingMatch = line.match(HEADING_PATTERN);
    if (headingMatch) {
      return {
        type: "heading",
        level: headingMatch[1].length,
        segments: parseInlineMarkdown(headingMatch[2]),
      };
    }
    if (CHOICE_MARKER_PATTERN.test(line)) {
      return { type: "choice-marker" };
    }
    const card = parseCardLine(line);
    if (card) {
      return { type: "card", card };
    }
    const chipMatch = line.match(CHIP_LINE_PATTERN);
    if (chipMatch) {
      return { type: "chip", text: chipMatch[1].trim() };
    }
    const bulletMatch = line.match(BULLET_PATTERN);
    if (bulletMatch) {
      return { type: "bullet", segments: parseInlineMarkdown(bulletMatch[1]) };
    }
    const orderedMatch = line.match(ORDERED_PATTERN);
    if (orderedMatch) {
      return { type: "ordered", segments: parseInlineMarkdown(orderedMatch[2]) };
    }
    return { type: "paragraph", segments: parseInlineMarkdown(line) };
  });
}

// The text's last line, up to (but not including) an unclosed trailing
// `[[` - or the line unchanged if every `[[` on it already has a matching
// `]]`. Marker-agnostic on purpose: this doesn't know or care whether the
// unclosed marker would eventually be a card/chip/choice/listing-ref/
// photo-ref/mls/draft/anything a future prompt change adds - any of them,
// still being typed out token by token, briefly reads as raw bracket
// syntax otherwise. A block marker (card/chip/choice) is the visible case
// (it occupies its own whole line, so the raw text was a jarring line of
// its own rather than a few stray mid-sentence characters), but the same
// truncation is correct for a trailing inline marker too - it just trims
// less, since only the unclosed tail itself is ever removed, never
// anything that precedes it on the line.
// listing-ref and photo-ref segments have no `.value` (they carry
// refType/refId/label or url/label instead) but always render visible
// content (a link's label, or an image), so they count as "has text" same
// as a non-blank text/bold segment.
function segmentsHaveText(segments) {
  return segments.some((s) =>
    s.type === "listing-ref" || s.type === "photo-ref"
      ? true
      : s.value.trim().length > 0
  );
}

function withoutTrailingUnclosedMarker(lastLine) {
  const openIdx = lastLine.lastIndexOf("[[");
  if (openIdx !== -1) {
    const closeIdx = lastLine.indexOf("]]", openIdx);
    if (closeIdx !== -1) return lastLine; // already closed - nothing to hide
    return lastLine.slice(0, openIdx);
  }
  // No "[[" yet, but a lone trailing "[" is the frame one SSE chunk before
  // that arrives - an unrelated chunk boundary can just as easily land
  // between the two brackets, so it flashes the same way if left alone.
  if (lastLine.endsWith("[")) return lastLine.slice(0, -1);
  return lastLine;
}

// Groups consecutive bullet lines into a single "bullet-group" block so the
// renderer can emit one <ul> instead of one per line. Drops empty paragraph
// lines (no visible text) rather than rendering blank <p> elements.
//
// Always hides an unclosed `[[...` marker still on the text's own last line
// (see withoutTrailingUnclosedMarker above) - scoped to only that last line,
// never an earlier one: an earlier line with an unclosed `[[` and real
// content after it is a settled malformation (the model genuinely emitted a
// broken marker), not a truncation artifact, and keeps showing as plain
// text same as before this existed.
//
// This used to run only while `streaming` was true, on the theory that a
// SETTLED reply would never end in a dangling "[[card:...". In practice a
// reply also settles mid-marker - the stream fails, the user hits Stop, or
// the request is aborted mid-token - and once that happens `streaming`
// flips (or was never true to begin with, e.g. the message is reloaded from
// history) while the marker is still sitting there unclosed, so a
// caller-gated check would leave it rendered forever as raw bracket syntax
// (a bug caught in review). An unclosed marker at the very end of ANY text -
// streaming, stopped, or fully settled - is never valid content worth
// showing raw, so the stripping itself is now unconditional.
//
// `streaming` is still accepted as a parameter (HelpChatPanel still passes
// it) in case a genuinely streaming-only behavior is added to this function
// later, but nothing here currently branches on it.
export function groupMarkdownBlocks(text, { streaming = false } = {}) {
  let effectiveText = text;
  if (text) {
    const lastNewline = text.lastIndexOf("\n");
    const lastLineStart = lastNewline + 1; // 0 when there's no "\n" at all
    const trimmedLastLine = withoutTrailingUnclosedMarker(
      text.slice(lastLineStart)
    );
    effectiveText = text.slice(0, lastLineStart) + trimmedLastLine;
  }
  const lines = parseMarkdownLines(effectiveText);
  const blocks = [];
  let currentGroup = null;
  // Set by a choice-marker line, consumed by the very next card-group this
  // loop starts (not every card-group after it) - a stray marker with no
  // card line right after it (model error, or the tail end of a stream)
  // just quietly does nothing rather than tagging some later, unrelated
  // group of cards as selectable.
  let pendingChoice = false;

  for (const line of lines) {
    if (line.type === "choice-marker") {
      pendingChoice = true;
      continue;
    }
    if (line.type === "bullet" || line.type === "ordered") {
      // A trailing bullet/ordered line truncated down to nothing by
      // withoutTrailingUnclosedMarker (its only content was the
      // now-hidden marker) would otherwise render as a bare bullet dot
      // or number with an empty <li> - skip it the same way an emptied
      // paragraph already is below, rather than starting (or leaving
      // dangling) a group for it.
      if (!segmentsHaveText(line.segments)) {
        pendingChoice = false;
        continue;
      }
      const groupType =
        line.type === "bullet" ? "bullet-group" : "ordered-group";
      if (!currentGroup || currentGroup.type !== groupType) {
        currentGroup = { type: groupType, items: [] };
        blocks.push(currentGroup);
      }
      currentGroup.items.push(line.segments);
      pendingChoice = false;
      continue;
    }
    if (line.type === "card") {
      // Consecutive cards (e.g. "show me my properties with photos" listing
      // several) group into one card-group so the renderer emits one list
      // container instead of one per card - same grouping shape as
      // bullet-group/ordered-group above, just with card objects as items
      // instead of segment arrays.
      if (!currentGroup || currentGroup.type !== "card-group") {
        currentGroup = { type: "card-group", items: [], selectable: pendingChoice };
        blocks.push(currentGroup);
      }
      currentGroup.items.push(line.card);
      pendingChoice = false;
      continue;
    }
    if (line.type === "chip") {
      // Consecutive chips group the same way - one row of buttons instead
      // of one per line.
      if (!currentGroup || currentGroup.type !== "chip-group") {
        currentGroup = { type: "chip-group", items: [] };
        blocks.push(currentGroup);
      }
      currentGroup.items.push(line.text);
      pendingChoice = false;
      continue;
    }
    if (line.type === "heading") {
      // Same emptied-by-truncation case as bullet/ordered above - an
      // empty heading still carries `chat-heading`'s margins, so it'd
      // flash as a blank gap rather than nothing at all. Doesn't reset
      // currentGroup/pendingChoice when empty, for the same reason a
      // genuinely blank line below doesn't: it produces no block of its
      // own, so it must not count as an "interrupting" line either.
      if (segmentsHaveText(line.segments)) {
        currentGroup = null;
        pendingChoice = false;
        blocks.push({
          type: "heading",
          level: line.level,
          segments: line.segments,
        });
      }
      continue;
    }
    // A blank (or truncated-to-empty) line resets nothing - it produces no
    // block of its own (the `if (segmentsHaveText(...))` below never pushes
    // one for it), so it must not count as the "interrupting" line that
    // ends a group either. Without this, a model that puts a blank line
    // after `[[choice]]` (a very common LLM formatting habit) silently
    // loses `pendingChoice` before the card line ever arrives, and the
    // resulting card-group renders as ordinary clickable links instead of
    // a `[[choice]]` prompt's radio options - a model formatting choice
    // degrading into a broken "answering this navigates away" UI, not a
    // cosmetic difference (a bug caught in review). The same
    // reset-on-blank-line bug also fragmented a run of cards/bullets/chips
    // into multiple one-item groups whenever the model put a blank line
    // between them.
    if (segmentsHaveText(line.segments)) {
      currentGroup = null;
      pendingChoice = false;
      blocks.push({ type: "paragraph", segments: line.segments });
    }
  }
  return blocks;
}

// The screen-reader live region announces the assistant's text as it lands.
// The raw text still carries the markers that become cards and chips on
// screen, so without this a screen reader read out
// "[[card:listing:94087|3BR 2BA in Lubbock, TX|Up to $500,000|3|2||..." one
// pipe at a time. Sighted users never saw it; everyone else heard all of it.
export function textForAnnouncement(text) {
  if (!text) return "";
  const str = String(text);
  // Same trailing-unclosed-marker guard groupMarkdownBlocks applies (see
  // above) - without it, a partial marker mid-stream (e.g.
  // "[[card:mls:1|Home|$1|3|2|1500|Lub") was mostly left UNCHANGED by the
  // replace() calls below, since they all require a closing "]]" to match -
  // so a screen reader read the raw pipe-delimited syntax aloud verbatim (a
  // gap caught in review). Applied before the marker-stripping regexes, to
  // the last line only, same scoping as groupMarkdownBlocks.
  const lastNewline = str.lastIndexOf("\n");
  const lastLineStart = lastNewline + 1;
  const trimmedLastLine = withoutTrailingUnclosedMarker(
    str.slice(lastLineStart)
  );
  const settled = str.slice(0, lastLineStart) + trimmedLastLine;
  return settled
    .replace(/\[\[choice\]\]/g, "")
    // The trailing fields are `|`-delimited, so each one is matched as a
    // pipe followed by a non-pipe run rather than by a second unbounded
    // `[^\]]*` - that second class is a superset of the label capture's own
    // `[^|\]]*`, and stacking two unbounded quantifiers over overlapping
    // character classes is quadratic-time to fail on unterminated input
    // (no closing `]]`), which is exactly the normal mid-stream state for a
    // card marker still being typed out (a bug caught in review).
    // Each capture/skip group below is bounded to 300 chars for the same
    // reason LISTING_REF_PATTERN/PHOTO_REF_PATTERN are above: unbounded,
    // a reply with many unterminated markers (not just one) re-scans to
    // end-of-string from every "[[" start position that never closes,
    // which is quadratic in the number of such positions, not just the
    // length of one marker (a bug caught in review - the fix that
    // resolved the *single*-marker case above didn't cover this one).
    .replace(/\[\[card:[^|\]]{0,300}:[^|\]]{0,300}\|([^|\]]{0,300})(?:\|[^|\]]{0,300}){0,20}\]\]/g, "$1")
    .replace(/\[\[chip:([^\]]{0,300})\]\]/g, "$1")
    .replace(/\[\[photo:[^|\]]{0,300}\|([^\]]{0,300})\]\]/g, "$1")
    .replace(/\[\[[a-z]+:[^|\]]{0,300}\|([^\]]{0,300})\]\]/g, "$1")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}
